/**
 * P12.4 — Approved Project Plan resolver
 *
 * Single source of truth for the plan that crews/contractors actually see.
 *
 * Resolution priority:
 *   systemPlan ← derived from getJobBriefing() + job.payoutAmount
 *   overridePlan ← latest archive entry for jobId (if any)
 *   finalPlan ← override values overlayed on system; falls back to system
 *
 * Hard rules:
 *   • Never exposes contractValue.
 *   • Never mutates Job.
 *   • System estimate is preserved verbatim.
 */

import type { Job } from "@/data/types";
import type { JobSpec } from "@/data/jobSpec";
import { getJobBriefing } from "@/data/contractor/jobBriefing";
import { getLatestOverrideForJob } from "./archive";
import type {
  ApprovedProjectPlan,
  PlanningSystemEstimateSnapshot,
} from "./types";

export function deriveSystemEstimate(
  job: Job,
  spec?: JobSpec,
): PlanningSystemEstimateSnapshot {
  const brief = getJobBriefing(job, spec);
  return {
    days: brief.estimatedDays,
    crewSize: brief.suggestedCrewSize,
    payoutAmount: job.payoutAmount ?? 0,
    riskFlags: brief.riskFlags.map((f) => f.key),
    arrival: brief.arrival.label,
    returnVisit: undefined,
  };
}

export function getApprovedProjectPlan(
  job: Job,
  spec?: JobSpec,
): ApprovedProjectPlan {
  const systemPlan = deriveSystemEstimate(job, spec);
  const review = job.planningReview;
  const archived = getLatestOverrideForJob(job.id);

  // Override is "active" only when an archive entry exists AND the job
  // review record points at it. Otherwise we treat it as a stale draft.
  const hasActiveOverride =
    !!archived &&
    !!review?.archiveEntryId &&
    review.archiveEntryId === archived.id;

  const overridePlan = hasActiveOverride ? archived!.override : undefined;

  const finalPlan = hasActiveOverride
    ? archived!.finalApproved
    : {
        days: systemPlan.days,
        crewSize: systemPlan.crewSize,
        payoutAmount: systemPlan.payoutAmount,
      };

  return {
    systemPlan,
    overridePlan,
    finalPlan,
    reviewStatus: review?.status ?? "pending",
    wasOverridden: hasActiveOverride,
    approvedBy: review?.approvedBy,
    approvedAt: review?.approvedAt,
    reviewedAt: review?.approvedAt,
    archiveEntryId: review?.archiveEntryId,
  };
}

/** Convenience predicate used by crew visibility gates. */
export function isApprovedForCrew(job: Job): boolean {
  return job.planningReview?.status === "approved";
}
